import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Globe } from "lucide-react";
import { slugifyOutlet, staticLogoForName } from "@/lib/guaranteedOutlets";

/**
 * Resolve the click destination for a Media Network card.
 *  - Guaranteed placement with has_outlet_page true  → /outlets/<slug>
 *  - Guaranteed placement with has_outlet_page false → https://digital24.com/register
 *  - Newswire (except the Gulf newswire)              → /services/newswire-distribution
 *  - Gulf newswire record                             → /gulf-newswire
 */
function cardHref(outlet) {
  if (outlet.type === "newswire") {
    const norm = (outlet.name || "").toLowerCase().trim().replace(/\s+/g, " ");
    if (norm.includes("gulf")) return "/gulf-newswire";
    return "/services/newswire-distribution";
  }
  if (outlet.has_outlet_page) {
    return `/outlets/${slugifyOutlet(outlet.name)}`;
  }
  return "https://digital24.com/register";
}

/**
 * Identity chip for Media Network cards. Logo source, in priority order:
 *   1. staticLogoForName(name) — the curated static registry
 *   2. the record's logo_url (favicon)
 *   3. existing text rendering (letter placeholder / newswire globe)
 * A failed image load (onError) falls through to the text rendering so no
 * broken image is ever shown. No brightness or colour filter is applied.
 */
function IdentityChip({ outlet }) {
  const [logoError, setLogoError] = useState(false);
  const logo = staticLogoForName(outlet.name) || outlet.favicon || null;

  if (logo && !logoError) {
    return (
      <span className="flex items-center justify-center w-6 h-6 rounded-md overflow-hidden shrink-0">
        <img
          src={logo}
          alt=""
          className="max-w-6 max-h-6 w-auto h-auto"
          onError={() => setLogoError(true)}
        />
      </span>
    );
  }

  if (outlet.type === "newswire") {
    return (
      <span className="flex items-center justify-center w-6 h-6 rounded-md shrink-0 bg-cobalt/10">
        <Globe className="w-3.5 h-3.5 text-cobalt" />
      </span>
    );
  }

  return (
    <span className="flex items-center justify-center w-6 h-6 rounded-md shrink-0 bg-[#f1eff5] text-steel text-xs font-semibold">
      {(outlet.name || "?").charAt(0).toUpperCase()}
    </span>
  );
}

function tierBadgeClass(tier) {
  return tier === "Tier 1 Publication"
    ? "bg-cobalt/10 text-cobalt border border-cobalt/20"
    : "bg-white text-steel border border-border";
}

export default function MediaNetworkCard({ outlet }) {
  const href = cardHref(outlet);
  const isExternal = href.startsWith("http");
  const className =
    "relative flex items-start justify-between bg-slate/30 border border-border rounded-lg px-5 py-4 hover:border-cobalt/30 transition-colors cursor-pointer block";

  const inner = (
    <>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2.5 mb-2">
          <IdentityChip outlet={outlet} />
          <span className="text-bone text-[17px] font-semibold font-heading truncate min-w-0">
            {outlet.name}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {outlet.region && <span className="meta-tag text-[9px]">{outlet.region}</span>}
          {outlet.sector && <span className="meta-tag text-[9px]">{outlet.sector}</span>}
        </div>
      </div>
      {outlet.tier && (
        <span
          className={`font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm shrink-0 ml-3 ${tierBadgeClass(outlet.tier)}`}
        >
          {outlet.tier}
        </span>
      )}
    </>
  );

  if (isExternal) {
    return (
      <a href={href} className={className}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={href} className={className}>
      {inner}
    </Link>
  );
}